import { Link, useLocation, useNavigate } from 'react-router';
import { Home, BookOpen, Coins, User, LogOut } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';

export function StudentNav() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const navItems = [
    {
      label: 'Dashboard',
      path: '/student/dashboard',
      icon: Home,
      emoji: null,
    },
    {
      label: 'The Archive',
      path: '/student/books',
      icon: BookOpen,
      emoji: '📖',
    },
    {
      label: 'The Path',
      path: '/student/coins',
      icon: Coins,
      emoji: '📜',
    },
    {
      label: 'The Bazaar',
      path: '/student/coins#shop',
      icon: null,
      emoji: '🛍️',
    },
    {
      label: 'The Vault',
      path: '/student/coins#bank',
      icon: null,
      emoji: '🏦',
    },
    {
      label: 'BizBook',
      path: '/student/bizbook',
      icon: null,
      emoji: '📘',
    },
  ];

  const isActive = (path: string) => location.pathname === path;

  return (
    <nav className="bg-white shadow-sm border-b-4 border-rainbow-gradient sticky top-0 z-50">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          {/* Logo/Brand */}
          <div className="flex items-center gap-3">
            <img
              src="/images/tisa-logo.png"
              alt="TISA Logo"
              className="w-10 h-10"
            />
            <span className="text-2xl font-child-friendly font-bold bg-gradient-to-r from-purple-600 via-blue-600 to-red-600 bg-clip-text text-transparent">
              TISAverse
            </span>
          </div>

          {/* Navigation Links */}
          <div className="flex items-center gap-2">
            {navItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path.split('#')[0]);

              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`
                    flex items-center gap-2 px-4 py-2 rounded-lg
                    font-child-friendly font-semibold text-sm
                    transition-all duration-200
                    ${active
                      ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-md'
                      : 'text-gray-700 hover:bg-purple-50 hover:text-purple-600'
                    }
                  `}
                >
                  {item.emoji ? (
                    <span className="text-lg">{item.emoji}</span>
                  ) : Icon ? (
                    <Icon className="w-5 h-5" />
                  ) : null}
                  <span className="hidden md:inline">{item.label}</span>
                </Link>
              );
            })}
          </div>

          {/* User Menu */}
          <div className="flex items-center gap-3">
            <div className="hidden lg:flex flex-col items-end">
              <span className="text-sm font-child-friendly font-bold text-gray-700">
                {user?.name}
              </span>
              <span className="text-xs text-gray-500">
                {user?.class_id !== null ? `Grade ${user?.class_id}` : ""}
              </span>
            </div>

            <div className="flex items-center gap-2">
              <Link
                to="/student/bizbook"
                className="p-2 rounded-full hover:bg-purple-50 text-purple-600 transition-colors"
                title="TISA BizBook"
              >
                <User className="w-5 h-5" />
              </Link>

              <button
                onClick={handleLogout}
                className="p-2 rounded-full hover:bg-red-50 text-red-500 transition-colors"
                title="Logout"
              >
                <LogOut className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Rainbow gradient border effect */}
      <style>{`
        .border-rainbow-gradient {
          border-image: linear-gradient(to right,
            #ef4444, #f59e0b, #10b981, #3b82f6, #8b5cf6, #ec4899
          ) 1;
        }
      `}</style>
    </nav>
  );
}
